import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { FloatingLeftButton } from "@/components/FloatingLeftButton";

import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useChangeLog } from "@/lib/hooks/useApi";
import type { ChangeLogResponse } from "@/lib/types/api";
import AnimatedLoading from "@/components/AnimatedLoading";
import { useTranslation } from "@/lib/hooks/useTranslation";
import {
  translateChangelogTitle,
  translateChangelogDescription,
  translateChangelogChange,
} from "@/lib/changelogTranslations";

const CHANGELOG_VISITED_KEY = "cfl:changelog:visited";

export const Route = createFileRoute("/(game)/changelog/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { data, isLoading, isError } = useChangeLog();
  const changeLogEntries = useMemo(() => data ?? [], [data]);
  const navigate = useNavigate();
  const [selectedVersion, setSelectedVersion] = useState<string>("");
  const { t } = useTranslation();

  // Mark changelog as visited when page loads
  useEffect(() => {
    if (typeof window === "undefined") return;
    localStorage.setItem(CHANGELOG_VISITED_KEY, "true");
  }, []);

  useEffect(() => {
    if (!changeLogEntries.length) {
      return;
    }

    setSelectedVersion((prev) => {
      if (!prev) {
        return changeLogEntries[0].id;
      }

      const stillExists = changeLogEntries.some(
        (entry: ChangeLogResponse) => entry.id === prev,
      );
      return stillExists ? prev : changeLogEntries[0].id;
    });
  }, [changeLogEntries]);

  const selectedChangelog = useMemo(() => {
    return changeLogEntries.find(
      (entry: ChangeLogResponse) => entry.id === selectedVersion,
    );
  }, [changeLogEntries, selectedVersion]);

  const formattedReleaseDate = useMemo(() => {
    if (!selectedChangelog?.releasedAt) {
      return null;
    }

    const releaseDate = new Date(selectedChangelog.releasedAt);
    return releaseDate.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }, [selectedChangelog]);

  if (isLoading) {
    return <AnimatedLoading />;
  }

  if (isError) {
    return (
      <div className="flex-1 min-h-0 flex items-center justify-center text-red-300 text-sm text-center">
        {t("changelog.error")}
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col gap-8 relative bg-[#1D2A52] pb-12">
      <FloatingLeftButton
        onClick={() => navigate({ to: "/home" })}
        className="absolute left-0 top-4 z-50"
        icon={
          <svg
            className="w-4 h-4"
            viewBox="0 0 9 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
              fill="white"
            />
          </svg>
        }
      />

      {/* Title */}
      <div className="mt-16 px-3 flex flex-col gap-2 min-w-full items-center justify-center">
        <h2 className="text-3xl font-bold text-white uppercase text-center">
          {t("changelog.title")}
        </h2>
        {/* Version Selector */}
        <div className="w-full">
          <Select
            value={selectedVersion}
            onValueChange={setSelectedVersion}
            disabled={!changeLogEntries.length}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t("changelog.selectVersion")} />
            </SelectTrigger>
            <SelectContent className="max-w-sm sm:max-w-md max-h-72 sm:max-h-120!">
              {changeLogEntries.map((entry: ChangeLogResponse) => (
                <SelectItem
                  key={entry.id}
                  value={entry.id}
                  className="items-start!"
                >
                  <div className="flex items-start gap-1">
                    <span className="min-w-10 text-end text-white/75">
                      {entry.version}
                    </span>
                    <span className="text-white/75">•</span>
                    <span className="ms-0.5">
                      {translateChangelogTitle(entry.title, entry.id, entry.version)}
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Changelog Content */}
      <div className="flex-1 min-h-0 mt-4 px-3 flex flex-col">
        {!selectedChangelog ? (
          <div className="flex-1 min-h-0 flex items-center justify-center text-white/70 text-sm">
            {t("changelog.noEntries")}
          </div>
        ) : (
          <ScrollArea className="flex-1 min-h-0">
            <div className="flex flex-col gap-8">
              {/* Changelog Header */}
              <div className="flex flex-col items-start justify-start">
                <div className="flex items-center gap-2 text-xs text-white/50">
                  <span>{t("changelog.releaseDate")}</span>
                  <span className="text-[#085Ab6]">
                    {formattedReleaseDate ?? t("changelog.tbd")}
                  </span>
                </div>
                <h3 className="text-2xl font-bold text-white uppercase">
                  {translateChangelogTitle(selectedChangelog.title, selectedChangelog.id, selectedChangelog.version)}
                </h3>
              </div>
              <p className="w-full text-white/85">
                {translateChangelogDescription(selectedChangelog.description, selectedChangelog.id, selectedChangelog.version)}
              </p>
              <div className="flex flex-col gap-0">
                <h6 className="text-sm text-[#085Ab6]">{t("changelog.changesNotes")}</h6>
                <ul className="flex flex-col gap-3 mt-2">
                  {selectedChangelog.changes.map(
                    (change: string, index: number) => (
                      <li
                        key={index}
                        className="flex items-start gap-2 text-white text-sm"
                      >
                        <span
                          className={cn(
                            "mt-2 shrink-0 w-1.5 h-1.5 rounded-full bg-[#085AB6]",
                          )}
                        />
                        <span className="flex-1 leading-relaxed">
                          {translateChangelogChange(change, selectedChangelog.id, index, selectedChangelog.version)}
                        </span>
                      </li>
                    ),
                  )}
                </ul>
              </div>
            </div>
          </ScrollArea>
        )}
      </div>

    </div>
  );
}
